import "./InvestmentDetails.css";

import { DealSpecificProperty, InvestmentDetailsInterface, SelectData } from "../../../../../../../models";
import { camelCaseToSentenceCase } from "../../../../../../../utils";
import { Column, MultiSelect, ReadonlyText, Row, Select, TextArea } from "../../../../../../../widget";
import { DynamicInvestmentDetails } from "../../../../../new-deal-journey/DealStructure/components/InvestmentDetails/components/DynamicInvestmentDetails/DynamicInvestmentDetails";

interface InvestmentDetailsProps {
  isEditing: boolean;
  selectedInvestment: InvestmentDetailsInterface | undefined;
  instrumentSelectData: SelectData;
  associationSelectData: SelectData;
  dealSpecificSchemaProperties: [string, DealSpecificProperty][] | undefined;
  hasValidationError: (investmentKey: string | undefined, fieldKey: string) => boolean;
  onUpdateInvestment: <K extends keyof InvestmentDetailsInterface>(
    key: string,
    field: K,
    value:
      | InvestmentDetailsInterface[K]
      | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
  ) => void;
}

export const InvestmentDetails = ({
  isEditing,
  selectedInvestment,
  instrumentSelectData,
  associationSelectData,
  dealSpecificSchemaProperties,
  hasValidationError,
  onUpdateInvestment,
}: InvestmentDetailsProps): JSX.Element => {
  const hasSelectedInvestment = Boolean(selectedInvestment);

  return (
    <div className="DealStructureCard">
      <div className="InvestmentDetailsCardHeader">
        <h2>Investment details</h2>
      </div>
      {!hasSelectedInvestment ? (
        <p className="body2 DealStructurePanelEmpty">Select an investment to view its details.</p>
      ) : (
        <>
          <Row spacingV="l">
            <Column span={6}>
              {isEditing && selectedInvestment ? (
                <Select
                  label="Instrument"
                  data={instrumentSelectData}
                  value={selectedInvestment.instrument}
                  hasError={hasValidationError(selectedInvestment.key, "instrument")}
                  onChange={(value) => {
                    onUpdateInvestment(selectedInvestment.key, "instrument", value);
                    onUpdateInvestment(selectedInvestment.key, "fields", {});
                  }}
                />
              ) : (
                <ReadonlyText
                  label="Instrument"
                  value={
                    selectedInvestment?.instrument ? camelCaseToSentenceCase(selectedInvestment.instrument) : undefined
                  }
                />
              )}
            </Column>
            {isEditing && selectedInvestment ? (
              <Column span={6}>
                <MultiSelect
                  label="Associations"
                  placeholder="Select"
                  data={associationSelectData}
                  values={selectedInvestment.associations}
                  hasError={hasValidationError(selectedInvestment.key, "associations")}
                  onChange={(value) => onUpdateInvestment(selectedInvestment.key, "associations", value)}
                />
              </Column>
            ) : null}
          </Row>
          <Row spacingV="l">
            <Column span={12}>
              {isEditing && selectedInvestment ? (
                <TextArea
                  label="Description"
                  name="dealStructureDescription"
                  placeholder="Enter a description..."
                  value={selectedInvestment.description}
                  onChange={(value) => onUpdateInvestment(selectedInvestment.key, "description", value)}
                />
              ) : (
                <ReadonlyText label="Description" value={selectedInvestment?.description} />
              )}
            </Column>
          </Row>
          <div className="DealStructureDynamicFields">
            <DynamicInvestmentDetails
              selectedInvestment={selectedInvestment}
              dealSpecificSchemaProperties={dealSpecificSchemaProperties}
              hasFieldError={(fieldKey) => hasValidationError(selectedInvestment?.key, fieldKey)}
              onUpdateInvestment={onUpdateInvestment}
              isReadonly={!isEditing}
            />
          </div>
        </>
      )}
    </div>
  );
};
